/**
 * This file holds actions. The actions commit mutations and can perform asynchronous operations.
 * Please refer to https://vuex.vuejs.org/guide/actions.html for more information
 */
import http from "@/http";
import axios from "axios";
import querystring from "querystring";
import returnUrlParams from "@/http/returnUrlParams";
import * as types from "./types";
const { VUE_APP_ENV: env, VUE_APP_ENDPOINTS: base } = process.env;

const actions = {
  [types.GET_AUDIENCE_SIZES](
    context,
    { partNumber, type = 'customizer' }
  ) {
    return new Promise((resolve, reject) => {
      let requestUrl = `${this.state.endPoints[type].sizes}${partNumber}`;
      const params = {};
      if (!this.state.utils.caid) {
        const {locale} = context.state.configuration;
        if (locale) params.locale = locale;
      }

      if (!context.getters.noEsiActive && !context.getters.vsfActive) {
        const { catalogId, storeId, langId } = context.state.configuration;
        if (catalogId) params.catalogId = catalogId;
        if (storeId) params.storeId = storeId;
        if (langId) params.langId = langId;
      }
      http
        .get(requestUrl, { params })
        .then(({ data }) => {
          context.commit(types.SET_AUDIENCE_SIZES, data);
          resolve(data);
        })
        .catch(reject);
    });
  },
  [types.GET_PART_NUMBER](context, { locale, recipeId, size }) {
    return new Promise((resolve, reject) => {
      let requestUrl = `${this.state.endPoints.customizer.part_number}${recipeId}`;
      const params = {};
      if (!this.state.utils.caid) params.locale = locale;
      if (size) params.size = size;
      http
        .get(requestUrl, { params })
        .then(({ data }) => {
          context.commit(types.SET_PART_NUMBER, data.sku);
          resolve(data.sku);
        })
        .catch(reject);
    });
  },
  [types.GET_GCC](context, { submission_id }) {
    return new Promise((resolve, reject) => {
      let requestUrl = `${this.state.endPoints.customizer.gcc}${submission_id}`;
      const params = {};
      http
        .get(requestUrl, { params })
        .then(({ data }) => {
          resolve(data);
        })
        .catch(reject);
    });
  },
  [types.GET_PDM]({ dispatch, commit, getters, state }, { type = 'customizer', setData = true, modelName = '' }) {
    const model = modelName ?  modelName : state.pdmModel;
    const domain = (this.state.utils.configuration.domain) ? this.state.utils.configuration.domain : process.env.VUE_APP_DOMAIN;
    const { env : utilsEnv } = this.state.utils;
    const pdm = `${domain}${base.replace(/\{ENV\}/g, utilsEnv)}/global_pdms/${model}.json`;
    return new Promise((resolve, reject) => http
      .get(getters.addCacheBusting(pdm))
      .then(({ data }) => {
        if (setData) commit(types.SET_PDM, data);
        resolve(data);
      })
      .catch(reject)
    );
  },
  [types.GET_CONFIGURATION](context, type = 'customizer') {
    const { caid } = this.state.utils;
    const domain = this.state.utils.configuration.domain || process.env.VUE_APP_DOMAIN;
    const { env : utilsEnv } = this.state.utils;

    const url = `${domain}${base.replace(/\{ENV\}/g, utilsEnv)}/configurations/${caid}/configuration.json`;
    return new Promise((resolve, reject) => {
      http
        .get(context.getters.addCacheBusting(url))
        .then(({ data }) => {
          data.domain = domain || "";
          context.commit(types.SET_CONFIGURATION, data);
          resolve(data);
        })
        .catch(reject);
      }
    );
  },
  [types.GET_AVAILABLE_STICKERS](context, type = 'customizer') {
    const requestUrl = `${context.getters.addCacheBusting(this.state.endPoints[type].availableStickers)}`;
    return new Promise((resolve, reject) => {
      if (
        context.state &&
        context.state.availableStickers &&
        context.state.availableStickers.length
      ) {
        return resolve(context.state.availableStickers);
      }
      http
        .get(requestUrl)
        .then(({ data }) => {
          // TODO: Remove if statement when stickers can be filtered by id
          if (type === 'customizer') {
            context.commit(types.SET_AVAILABLE_STICKERS, data);
          }
          resolve(data);
        })
        .catch(reject);
    });
  },
  [types.GET_AVAILABLE_STICKER_GROUPS](context, type = 'customizer') {
    const requestUrl = `${context.getters.addCacheBusting(this.state.endPoints[type].availableStickersGroups)}`;
    return new Promise((resolve, reject) => {
      // TODO test this without promise
      http
        .get(requestUrl)
        .then(({ data }) => {
          context.commit(types.SET_AVAILABLE_STICKER_GROUPS, data);
          resolve(data);
        })
        .catch(reject);
    });
  },
  [types.GET_AVAILABLE_SWATCHES]({state, commit, dispatch, getters}, {setData = true}) {
    const params = {};
    const enablePerformanceLogging = this.state.utils.queryParams.enablePerformanceLogging || this.state.utils.configuration.enablePerformanceLogging
    // Temporary change for TMALL would be removed when swatches in db is live
    let requestUrl;
    if (this.state.endPoints.customizer.availableSwatchesMeta) {
      requestUrl = `${this.state.endPoints.customizer.availableSwatchesMeta}available_swatches.json`;
    } else {
      requestUrl = `${this.state.endPoints.customizer.availableSwatches}${state.pdmModel}.available_swatches.json`;
    }
    if(this.state.utils.configuration.enableDBSwatches){
      requestUrl = `${this.state.endPoints.customizer.availableSwatchesDB}${state.pdmModel}`
      if(this.state.utils.configuration.enableS3Customizer){
        requestUrl = `${requestUrl}.json`.replace(this.state.utils.configuration.domain,'');
      }
    }
    return new Promise((resolve, reject) => {
      http
        .get(getters.addCacheBusting(requestUrl), { params })
        .then(({ data }) => {
          if (setData) commit(types.SET_AVAILABLE_SWATCHES, data);
          dispatch('wait/end', 'loading swatches', { root: true });
          resolve(data);
        })
        .catch(reject);
    });
  },
  [types.GET_AVAILABLE_SWATCHES_BY_ID]({state, commit, dispatch, getters}, { swatches = [], swatchData = {}}) {
    const params = {};
    let requestUrl;

    const swatchIds = swatches.toString();
    requestUrl = `${this.state.endPoints.customizer.availableSwatchesById}${swatchIds}`;

    return new Promise((resolve, reject) => {
      http
        .get(getters.addCacheBusting(requestUrl), { params })
        .then(({ data }) => {
          //transform response to match swatchData
          const dataArr = [];
          Object.keys(data).forEach(key => {
            dataArr.push(data[key]);
          });
          const allSwatches = dataArr.concat(swatchData);
          commit(types.SET_AVAILABLE_SWATCHES, allSwatches);
          dispatch('wait/end', 'loading swatches', { root: true });
          resolve(data);
        })
        .catch(reject);
    });
  },
  [types.GET_AVAILABLE_SWATCHES_BY_SUBGROUP]({state, commit, dispatch, getters}, {appType = "customizer", setData = true, subgroups = 'solids-apparel,patterns-apparel,checkerboards-apparel,graphics'}) {
    const params = {};
    const requestUrl = `${this.state.endPoints[appType].availableSwatchesById}bygroup/${subgroups}`;

    return new Promise((resolve, reject) => {
      http
        .get(getters.addCacheBusting(requestUrl), { params })
        .then(({ data }) => {
          if (setData) commit(types.SET_AVAILABLE_SWATCHES, data);
          dispatch('wait/end', 'loading swatches', { root: true });
          resolve(data);
        })
        .catch(reject);
    });
  },
  [types.RETURN_AVERGAGE_RGB](contextVue, imgEl) {
    return new Promise(resolve => {
      let blockSize = 5, // only visit every 5 pixels
        canvas = document.createElement("canvas"),
        context = canvas.getContext && canvas.getContext("2d"),
        data,
        width,
        height,
        i = -4,
        length,
        rgb = {
          r: 0,
          g: 0,
          b: 0
        },
        count = 0;

      if (!context) {
        resolve(rgb);
      } else {
        height = canvas.height = imgEl.naturalHeight || imgEl.offsetHeight || imgEl.height;
        width = canvas.width = imgEl.naturalWidth || imgEl.offsetWidth || imgEl.width;

        context.drawImage(imgEl, 0, 0);

        try {
          data = context.getImageData(0, 0, width, height);

          length = data.data.length;

          while ((i += blockSize * 4) < length) {
            ++count;
            rgb.r += data.data[i];
            rgb.g += data.data[i + 1];
            rgb.b += data.data[i + 2];
          }

          // ~~ used to floor values
          rgb.r = ~~(rgb.r / count);
          rgb.g = ~~(rgb.g / count);
          rgb.b = ~~(rgb.b / count);
          resolve(rgb);
        } catch (e) {
          /* security error, img on diff domain */
          console.error(e);
          resolve(rgb);
        }
      }
    });
  },
  [types.SEND_GROUP_EMAIL](context, { data }) {
    const requestUrl = `${this.state.endPoints.customizer.shareGroupEmail}`;

    return new Promise((resolve, reject) => {
      axios({
        url: requestUrl,
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8"
        },
        data: querystring.stringify(data)
      })
        .then(({ data }) => {
          resolve(data);
        })
        .catch(reject);
    });
  },
  [types.ADD_TO_FAVORITES](context, { data }) {
    const requestUrl = `${
      this.state.endPoints.customizer.addToFavorites
    }?${returnUrlParams(data)}`;

    return new Promise((resolve, reject) => {
      axios({
        url: requestUrl,
        method: "POST",
        headers: { "content-type": "application/json" }
      })
        .then(({ data }) => {
          resolve(data);
        })
        .catch(reject);
    });
  },
  [types.GET_BUCKET_SIZE](
      context
  ) {
    return new Promise((resolve, reject) => {
      const endPoints = context.rootState.endPoints;
      const requestUrl = `${endPoints.customizer.s3ModelsBucket}unisex_size_map.json`;
      http.get(requestUrl)
          .then(({ data }) => {
            context.commit(types.SET_BUCKET_SIZE, data);
            resolve(data);
          })
          .catch(reject);
    });
  },
  [types.SHARE_IN_STORE](context, {sku, email, recipe_id}) {
    const endPoints = context.rootState.endPoints;
    const requestURL = `${endPoints.customizer.shareInStore}`;
    const inStoreForm = new FormData();
    inStoreForm.append('sku', sku);
    inStoreForm.append('email', email);
    inStoreForm.append('recipe_id', recipe_id);
    return new Promise((resolve, reject) => {
      axios({
        url: requestURL,
        method: "POST",
        data: inStoreForm
      })
        .then(({ data }) => {
          resolve(data);
        })
        .catch(reject);
    });
  },
  [types.GET_PATTEN_MAKER_AVAILABLE_CONTENT](context, type = 'customizer') {
    const { caid } = this.state.utils;
    const domain = this.state.utils.configuration.domain || process.env.VUE_APP_DOMAIN;
    const { env : utilsEnv } = this.state.utils;
    const url = `${domain}${base.replace(/\{ENV\}/g, utilsEnv)}/configurations/${caid}/pattern_maker.json`;
    http
      .get(context.getters.addCacheBusting(url))
      .then(({ data }) => {
        context.commit(types.SET_PATTEN_MAKER_AVAILABLE_CONTENT, data);
        return data;
      })
  },
  [types.CALL_FAVORITES_API](context, {recipe, sku, productImageUrl, quantity}) {
    const domain = this.state.utils.configuration.domain || process.env.VUE_APP_DOMAIN;
    const url = `${domain}/api/wishlists/v2/favorites`;

    return fetch(url, {
      headers: this.state.utils.canvasAPIheaders,
      method: "POST",
      body: JSON.stringify({
        favoriteId:'',
        recipeId: recipe,
        id: sku,
        itemType: 'product',
        pdpUrl: `${window.location.pathname}?recipe=${recipe}`,
        productImageUrl,
        qty: quantity,
        defaultProductView: '',
        public: true,
      })
    });
  },
  [types.FETCH_HEADERS_DATA](context){
    const baseURL = this.state.utils.configuration.domain || process.env.VUE_APP_DOMAIN;
    const caid = this.state.utils.caid;
    const env = this.state.utils.env;
    return fetch(`${baseURL}/${env}/api/v1/cart/domain/${caid}`, {
      method: "GET",
    }).then(response => response.json()).then((response)=> {
      const split = response.locale.split("_");
      response.locale = split[0] + "_" + split[1].toUpperCase();
      context.commit("SET_HEADERS_DATA", response)
    });
  },
  [types.FETCH_CUSTOMS_TOOL_DATA](context, { tool }) {
    const domain = (this.state.utils.configuration.domain) ? this.state.utils.configuration.domain : process.env.VUE_APP_DOMAIN;
    const { env : utilsEnv } = this.state.utils;
    const url = `${domain}${base.replace(/\{ENV\}/g, utilsEnv)}/entryPointsConfig/${tool.toLowerCase()}.json`;
    return fetch(url, {
      method: "GET",
    }).then(response => response.json()).then((response)=> {
      return response;
    })
  },
  [types.GET_PRODUCT_IMAGES_FROM_SCENE](context, { blob, recipeId, size, view }) {
    const params = {recipeId, size, view};
    const requestUrl = `${this.state.endPoints.customizer.snapshot}?${returnUrlParams(params)}`;
    const data = new FormData();
    data.append('file-input', blob, "snapshot.jpg");

    return new Promise((resolve, reject) => {
      http
        .post(requestUrl, data)
        .then(({ data }) => {
          resolve(data);

        })
        .catch(reject);
    });
  },
};

export default actions;
